/**
 * Workflows and their steps, as `/api/workflow-defns` serves them to the
 * conversation builder.
 *
 * A WORKFLOW is the order of steps and when each runs; a STEP is a component
 * with its settings and words. The graph shape here mirrors
 * `@vagusx/agent-core/v2`'s `authoring.ts`, which this package cannot import;
 * the API parses every draft through agent-core's own schema before storing
 * it, so a graph this schema accepts but the engine would not is refused
 * there, not silently saved.
 */

import { z } from 'zod';
import { COMPONENT_CALL_DIRECTIONS, COMPONENT_CHANNELS } from '../agent-components/types';
import { componentTestInputSchema } from '../agent-components/types';

// ── The graph ──────────────────────────────────────────────────────────────

const gateList = z.array(z.string().min(1)).min(1).optional();
const nodeId = z.string().regex(/^[A-Za-z0-9_-]+$/, 'node id must be alphanumeric with - or _');

/** A cleared text box sends `""`; for a purpose that means "none". */
const intentField = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() === '' ? null : v),
  z.string().trim().min(1).max(80).nullable().optional()
);

export const workflowNodeSchema = z
  .object({
    id: nodeId,
    /** The step this node runs. */
    stepId: z.uuid(),
    /** Facts that must all be recorded before this step runs. */
    requires: gateList,
    /** Facts any one of which skips this step. */
    unless: gateList,
    /**
     * Where the call goes next. An edge with `when` is taken only when this
     * step ended with one of those outcomes; the one without is the default,
     * and comes last (ADR-276).
     */
    transitions: z.array(
      z
        .object({
          to: nodeId,
          when: z
            .object({ outcome: z.array(z.string().min(1)).min(1) })
            .strict()
            .optional(),
        })
        .strict()
    ),
  })
  .strict();
export type WorkflowNode = z.infer<typeof workflowNodeSchema>;

export const workflowGraphSchema = z
  .object({
    entry: z.string().min(1),
    /** The step the call jumps to when a `closeWhen` fact is recorded. */
    close: z.string().min(1).optional(),
    closeWhen: gateList,
    /** Where a caller who asks, during any step, for a person or a callback goes. */
    anyTime: z
      .object({ handoff: nodeId.optional(), callback: nodeId.optional() })
      .strict()
      .optional(),
    nodes: z.array(workflowNodeSchema),
    config: z.record(z.string(), z.unknown()),
  })
  .strict();
export type WorkflowGraph = z.infer<typeof workflowGraphSchema>;

/** A step's words: local line name → text per language. */
export const lineContentSchema = z.record(
  z.string().min(1),
  z.object({ en: z.string().optional(), es: z.string().optional() }).strict()
);
export type LineContent = z.infer<typeof lineContentSchema>;

// ── Problems ───────────────────────────────────────────────────────────────

export const workflowProblemSchema = z
  .object({
    code: z.string().min(1),
    message: z.string().min(1),
    /** The node to show it on; absent for a problem with the whole workflow. */
    nodeId: z.string().optional(),
    /** The field within the node's step: `contentSlot`, `requires[0]`. */
    at: z.string().optional(),
    /** A warning never blocks a publish. */
    severity: z.literal('warning').optional(),
  })
  .strict();
export type WorkflowProblem = z.infer<typeof workflowProblemSchema>;

export const problemCountSchema = z
  .object({ errors: z.number().int().min(0), warnings: z.number().int().min(0) })
  .strict();
export type ProblemCount = z.infer<typeof problemCountSchema>;

// ── Rows ───────────────────────────────────────────────────────────────────

export const workflowStepSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    componentKey: z.string(),
    /** Null for a step in the shared library. */
    ownerWorkflowId: z.string().nullable(),
    config: z.record(z.string(), z.unknown()),
    content: lineContentSchema,
    revision: z.number().int(),
    updatedAt: z.coerce.date(),
  })
  .strict();
export type WorkflowStep = z.infer<typeof workflowStepSchema>;

export const workflowSummarySchema = z
  .object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
    description: z.string(),
    intent: z.string().nullable(),
    direction: z.enum(COMPONENT_CALL_DIRECTIONS),
    channel: z.enum(COMPONENT_CHANNELS),
    revision: z.number().int(),
    activeVersion: z
      .object({
        id: z.string(),
        version: z.number().int(),
        label: z.string(),
        publishedAt: z.coerce.date(),
      })
      .strict()
      .nullable(),
    /** The draft, or a step it uses, has changed since the live version. */
    hasUnpublishedChanges: z.boolean(),
    problems: problemCountSchema,
    updatedAt: z.coerce.date(),
  })
  .strict();
export type WorkflowSummary = z.infer<typeof workflowSummarySchema>;

export const workflowVersionSchema = z
  .object({
    id: z.string(),
    version: z.number().int(),
    label: z.string(),
    createdAt: z.coerce.date(),
    createdBy: z.string().nullable(),
  })
  .strict();
export type WorkflowVersion = z.infer<typeof workflowVersionSchema>;

export const workflowDetailSchema = workflowSummarySchema
  .extend({
    draft: workflowGraphSchema,
    /** Every step this workflow may use: its own, and the shared library. */
    steps: z.array(workflowStepSchema),
    versions: z.array(workflowVersionSchema),
    problemList: z.array(workflowProblemSchema),
    /**
     * Per node: the facts a step before it can have recorded by the time it
     * runs, plus what the call starts knowing. What a `requires` may name.
     */
    factsBefore: z.record(z.string(), z.array(z.string())),
    /** Every fact anything in this workflow can record. What `unless` may name. */
    factsAnywhere: z.array(z.string()),
  })
  .strict();
export type WorkflowDetail = z.infer<typeof workflowDetailSchema>;

// ── Requests ───────────────────────────────────────────────────────────────

export const createWorkflowBodySchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    description: z.string().max(500).optional(),
    intent: intentField,
    direction: z.enum(COMPONENT_CALL_DIRECTIONS),
    channel: z.enum(COMPONENT_CHANNELS),
  })
  .strict();
export type CreateWorkflowBody = z.infer<typeof createWorkflowBodySchema>;

export const updateWorkflowBodySchema = z
  .object({
    /** The `revision` the edit was made against; a stale one is refused. */
    expectedRevision: z.number().int(),
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().max(500).optional(),
    intent: intentField,
    draft: workflowGraphSchema.optional(),
  })
  .strict();
export type UpdateWorkflowBody = z.infer<typeof updateWorkflowBodySchema>;

export const publishWorkflowBodySchema = z
  .object({
    expectedRevision: z.number().int(),
    /** What changed, in words — shown in the version history. */
    label: z.string().trim().min(1).max(120),
  })
  .strict();
export type PublishWorkflowBody = z.infer<typeof publishWorkflowBodySchema>;

export const createStepBodySchema = z
  .object({
    componentKey: z.string().min(1),
    name: z.string().trim().min(1).max(120),
    /** Put it in the shared library instead of keeping it private to the workflow. */
    shared: z.boolean().optional(),
  })
  .strict();
export type CreateStepBody = z.infer<typeof createStepBodySchema>;

export const updateStepBodySchema = z
  .object({
    expectedRevision: z.number().int(),
    name: z.string().trim().min(1).max(120).optional(),
    config: z.record(z.string(), z.unknown()).optional(),
    content: lineContentSchema.optional(),
  })
  .strict();
export type UpdateStepBody = z.infer<typeof updateStepBodySchema>;

export const workflowListSchema = z.array(workflowSummarySchema);
export const workflowProblemListSchema = z.array(workflowProblemSchema);

// ── What a call runs (`POST /internal/workflow-defns/active`) ──────────────

/**
 * The published version a call should run for an intent, or null for "run the
 * code-defined workflow". The definition is served as stored; the worker
 * parses it with agent-core's own schema before anything runs.
 */
export const activeWorkflowSchema = z
  .object({
    workflow: z
      .object({
        versionId: z.string(),
        key: z.string(),
        version: z.number().int(),
        definition: z.record(z.string(), z.unknown()),
        content: lineContentSchema,
      })
      .strict()
      .nullable(),
  })
  .strict();
export type ActiveWorkflow = z.infer<typeof activeWorkflowSchema>;

// ── Testing a draft (ADR-275) ─────────────────────────────────────────────

/**
 * What the tester picked before a test call (ADR-306). One field per input kind
 * a component can declare (`testInputs` in the catalog); the API accepts only
 * the kinds the tested steps declare, and checks every id is the caller's.
 */
export const workflowTestInputsSchema = z
  .object({
    providerId: z.string().uuid().optional(),
    locationId: z.string().uuid().optional(),
    appointmentTypeId: z.string().uuid().optional(),
    /** A made-up appointment the test patient has. Nothing real is created. */
    existingAppointment: z
      .object({
        providerId: z.string().uuid(),
        locationId: z.string().uuid(),
        appointmentTypeId: z.string().uuid().optional(),
        /**
         * Wall-clock `YYYY-MM-DDTHH:mm` AT THE OFFICE, as a real appointment is
         * kept. The API places it in the office's own zone, so a tester in
         * another zone describes the same appointment a patient would hear.
         */
        startLocal: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/),
      })
      .strict()
      .optional(),
  })
  .strict();
export type WorkflowTestInputs = z.infer<typeof workflowTestInputsSchema>;

/**
 * What a test of the draft — or of one step — asks the tester for, as the API
 * will hold it to (ADR-306). Depends on the step's config, so it is asked of
 * the API rather than read from the catalog.
 */
export const workflowTestInputSpecsSchema = z
  .object({ inputs: z.array(componentTestInputSchema) })
  .strict();
export type WorkflowTestInputSpecs = z.infer<typeof workflowTestInputSpecsSchema>;

/** Test the whole draft, or with `nodeId` just that step. */
export const testWorkflowBodySchema = z
  .object({
    nodeId: z
      .string()
      .regex(/^[A-Za-z0-9_-]+$/)
      .optional(),
    inputs: workflowTestInputsSchema.optional(),
  })
  .strict();
export type TestWorkflowBody = z.infer<typeof testWorkflowBodySchema>;

// ── What a test call did (ADR-306) ─────────────────────────────────────────

/** The writes a test withholds. Mirrors agent-core's `TEST_ACTION_KINDS`. */
export const WORKFLOW_TEST_ACTION_KINDS = [
  'book',
  'reschedule',
  'cancel',
  'send_sms',
  'transfer',
] as const;

const actionDetailSchema = z
  .object({ label: z.string(), value: z.union([z.string(), z.number(), z.boolean(), z.null()]) })
  .strict();

/** A withheld write, as the worker records it. */
export const workflowTestActionSchema = z
  .object({
    kind: z.enum(WORKFLOW_TEST_ACTION_KINDS),
    node: z.string().nullable(),
    details: z.array(actionDetailSchema).max(20),
    sms: z
      .object({
        to: z.string().max(40),
        type: z.enum(['appointment_confirmation', 'custom']),
        data: z.record(z.string(), z.unknown()),
      })
      .strict()
      .optional(),
  })
  .strict();
export type WorkflowTestAction = z.infer<typeof workflowTestActionSchema>;

// `value` may be absent: a fact written as `undefined` loses its key in JSON,
// and one such fact must not cost the tester the whole report.
const factSchema = z
  .object({ fact: z.string(), label: z.string(), value: z.unknown().optional() })
  .strict();

/** One step of a finished test call, as the worker reports it. */
export const workflowTestStepSchema = z
  .object({
    node: z.string(),
    component: z.string(),
    outcome: z.string().nullable(),
    plan: z.enum(['stub', 'off']).nullable(),
    facts: z.array(factSchema).max(100),
    actions: z.array(workflowTestActionSchema).max(50),
  })
  .strict();

/** The body the worker posts when a test call's walk ends. */
export const workflowTestReportSchema = z
  .object({
    steps: z.array(workflowTestStepSchema).max(200),
    unattributed: z.array(workflowTestActionSchema).max(50),
  })
  .strict();
export type WorkflowTestReport = z.infer<typeof workflowTestReportSchema>;

/** A withheld write as the tester reads it: an SMS carries its rendered text. */
export const workflowTestResultActionSchema = z
  .object({
    kind: z.enum(WORKFLOW_TEST_ACTION_KINDS),
    details: z.array(actionDetailSchema),
    /** For `send_sms`: the exact text a real send would have sent. */
    message: z.string().nullable(),
  })
  .strict();
export type WorkflowTestResultAction = z.infer<typeof workflowTestResultActionSchema>;

export const workflowTestResultStepSchema = z
  .object({
    node: z.string(),
    /** The step's name in the builder, or what the test wrapped it with. */
    name: z.string(),
    component: z.string(),
    outcome: z.string().nullable(),
    plan: z.enum(['stub', 'off']).nullable(),
    facts: z.array(factSchema),
    actions: z.array(workflowTestResultActionSchema),
  })
  .strict();

/**
 * What a test call understood and withheld, for the test sheet. `report` is
 * null until the worker has posted it — the call may still be winding down.
 */
export const workflowTestResultSchema = z
  .object({
    report: z
      .object({
        steps: z.array(workflowTestResultStepSchema),
        unattributed: z.array(workflowTestResultActionSchema),
      })
      .strict()
      .nullable(),
  })
  .strict();
export type WorkflowTestResult = z.infer<typeof workflowTestResultSchema>;

// ── What a test call did, for a ticket (ADR-309) ───────────────────────────

/**
 * The bounds the worker holds a trace to while the call runs, and the API
 * holds it to on arrival. One test call is minutes long; past these, the
 * worker counts what it dropped instead of growing without limit.
 */
export const WORKFLOW_TEST_TRACE_LIMITS = {
  entries: 5000,
  /** Characters of one log line, or of one entry's JSON data. */
  text: 4000,
} as const;

const traceText = z.string().max(WORKFLOW_TEST_TRACE_LIMITS.text);
const traceAt = z.string().datetime();

/**
 * One thing the worker saw on a test call that the transcript does not hold
 * (ADR-309). The transcript already has every turn and tool call; this is the
 * worker's side of them.
 *
 * - `log`: a line the call's job process wrote to its console.
 * - `walk`: the walk moved to another step, or its status changed.
 * - `node`: a step's own state changed (status, outcome, private scratch).
 * - `wrote`: facts a step wrote (the runner's `onNodeWrote`).
 * - `state`: the agent or the caller started or stopped speaking.
 * - `error`: the session reported an error or closed with one.
 */
export const workflowTestTraceEntrySchema = z.discriminatedUnion('kind', [
  z
    .object({
      at: traceAt,
      kind: z.literal('log'),
      level: z.enum(['info', 'warn', 'error']),
      text: traceText,
    })
    .strict(),
  z
    .object({
      at: traceAt,
      kind: z.literal('walk'),
      status: z.string().max(40),
      node: z.string().max(200).nullable(),
    })
    .strict(),
  z
    .object({
      at: traceAt,
      kind: z.literal('node'),
      node: z.string().max(200),
      status: z.string().max(40),
      outcome: z.string().max(200).nullable(),
      /** Times the walk came back to this step. Absent from a worker older than it. */
      revisits: z.number().int().min(0).optional(),
      /** The step's private scratch, as JSON, cut to the text bound. */
      data: traceText,
    })
    .strict(),
  z
    .object({
      at: traceAt,
      kind: z.literal('wrote'),
      node: z.string().max(200),
      /** The facts, as JSON, cut to the text bound. */
      facts: traceText,
    })
    .strict(),
  z
    .object({
      at: traceAt,
      kind: z.literal('state'),
      who: z.enum(['agent', 'caller']),
      state: z.string().max(40),
    })
    .strict(),
  z
    .object({
      at: traceAt,
      kind: z.literal('error'),
      source: z.string().max(100),
      message: traceText,
    })
    .strict(),
]);
export type WorkflowTestTraceEntry = z.infer<typeof workflowTestTraceEntrySchema>;

/** What the worker posts when a test call's job ends (ADR-309). */
export const workflowTestTraceSchema = z
  .object({
    startedAt: traceAt,
    endedAt: traceAt,
    /** Which worker ran it: Cloud Run's revision, or `local`. */
    worker: z.string().max(200),
    /** What the tester picked, as the worker received it. */
    inputs: z.record(z.string(), z.unknown()).nullable(),
    entries: z.array(workflowTestTraceEntrySchema).max(WORKFLOW_TEST_TRACE_LIMITS.entries),
    /** Entries past the bound, counted rather than kept. */
    dropped: z.number().int().min(0),
  })
  .strict();
export type WorkflowTestTrace = z.infer<typeof workflowTestTraceSchema>;

/** One transcript row, as the capture carries it. */
export const workflowTestCaptureMessageSchema = z
  .object({
    at: z.string(),
    kind: z.string(),
    agentId: z.string().nullable(),
    text: z.string().nullable(),
    toolName: z.string().nullable(),
    toolStatus: z.string().nullable(),
    toolDurationMs: z.number().nullable(),
    payload: z.unknown(),
  })
  .strict();

/** One interaction event, as the capture carries it. */
export const workflowTestCaptureEventSchema = z
  .object({
    at: z.string(),
    type: z.string(),
    title: z.string(),
    data: z.unknown(),
    status: z.number(),
    error: z.string().nullable(),
  })
  .strict();

/**
 * Everything a finished test call left behind, in one piece, for the tester to
 * copy into a ticket (ADR-309): what was tested, the definition that ran, the
 * transcript and events the call wrote, the report, and the worker's trace.
 * Each part is null or empty when the call never got that far, and that is
 * itself the finding.
 */
export const workflowTestCaptureSchema = z
  .object({
    test: z
      .object({
        interactionId: z.string(),
        workflowId: z.string(),
        workflowName: z.string(),
        /** The step under test, or null for the whole workflow. */
        nodeId: z.string().nullable(),
        snapshotId: z.string(),
        fromDraftRevision: z.number(),
        status: z.string(),
        createdAt: z.string(),
        startedAt: z.string().nullable(),
        endedAt: z.string().nullable(),
      })
      .strict(),
    /** The frozen definition the call ran, with its words. */
    definition: z.unknown(),
    testPlan: z.unknown().nullable(),
    transcript: z.array(workflowTestCaptureMessageSchema),
    events: z.array(workflowTestCaptureEventSchema),
    report: workflowTestReportSchema.nullable(),
    trace: workflowTestTraceSchema.nullable(),
  })
  .strict();
export type WorkflowTestCapture = z.infer<typeof workflowTestCaptureSchema>;
